import { useState, useCallback } from 'react';

/**
 * Custom hook for managing loading state
 * @param initialState - Initial loading state (default: false)
 * @returns Loading state and control functions
 */
export function useLoading(initialState: boolean = false) {
  const [loading, setLoading] = useState(initialState);

  const startLoading = useCallback(() => {
    setLoading(true);
  }, []);

  const stopLoading = useCallback(() => {
    setLoading(false);
  }, []);

  const withLoading = useCallback(async <T>(asyncFn: () => Promise<T>): Promise<T> => {
    try {
      startLoading();
      const result = await asyncFn();
      return result;
    } finally {
      stopLoading();
    }
  }, [startLoading, stopLoading]);

  return {
    loading,
    setLoading,
    startLoading,
    stopLoading,
    withLoading,
  };
}

/**
 * Hook for managing multiple loading states
 * @param keys - Array of loading state keys
 * @returns Object with loading states and control functions
 */
export function useMultiLoading(keys: string[]) {
  const [loadingStates, setLoadingStates] = useState<Record<string, boolean>>(
    keys.reduce((acc, key) => ({ ...acc, [key]: false }), {})
  );

  const setLoading = useCallback((key: string, loading: boolean) => {
    setLoadingStates(prev => ({ ...prev, [key]: loading }));
  }, []);

  const startLoading = useCallback((key: string) => {
    setLoading(key, true);
  }, [setLoading]);

  const stopLoading = useCallback((key: string) => {
    setLoading(key, false);
  }, [setLoading]);

  const withLoading = useCallback(async <T>(
    key: string, 
    asyncFn: () => Promise<T>
  ): Promise<T> => {
    try {
      startLoading(key);
      const result = await asyncFn();
      return result;
    } finally {
      stopLoading(key);
    }
  }, [startLoading, stopLoading]);

  const isLoading = useCallback((key: string) => {
    return loadingStates[key] || false;
  }, [loadingStates]);

  const isAnyLoading = useCallback(() => {
    return Object.values(loadingStates).some(Boolean);
  }, [loadingStates]);

  return {
    loadingStates,
    setLoading,
    startLoading,
    stopLoading,
    withLoading,
    isLoading,
    isAnyLoading,
  };
}

/**
 * Hook for managing form submission loading
 * @returns Form loading state and control functions
 */
export function useFormLoading() {
  const { loading, startLoading, stopLoading, withLoading } = useLoading();

  const handleSubmit = useCallback(async <T>(
    submitFn: () => Promise<T>,
    onSuccess?: (result: T) => void,
    onError?: (error: any) => void
  ) => {
    try {
      const result = await withLoading(submitFn);
      onSuccess?.(result);
      return result;
    } catch (error) {
      onError?.(error);
      throw error;
    }
  }, [withLoading]);

  return {
    loading,
    startLoading,
    stopLoading,
    withLoading,
    handleSubmit,
  };
}

/**
 * Hook for managing modal loading state
 * @returns Modal loading state and control functions
 */
export function useModalLoading() {
  const { loading, startLoading, stopLoading, withLoading } = useLoading();

  const handleModalAction = useCallback(async <T>(
    actionFn: () => Promise<T>,
    onSuccess?: (result: T) => void,
    onError?: (error: any) => void
  ) => {
    try {
      const result = await withLoading(actionFn);
      onSuccess?.(result);
      return result;
    } catch (error) {
      onError?.(error);
      // Don't throw error to keep modal open
      return null;
    }
  }, [withLoading]);

  return {
    loading,
    startLoading,
    stopLoading,
    withLoading,
    handleModalAction,
  };
}
